// Bootstrap MapLibre: basi raster identiche alla v1 (OSM / Esri World Imagery),
// zoom frazionario continuo, inerzia, tutto GPU. Nessun glyph server: ogni
// testo in mappa è "baked" in immagini canvas (stessa resa della v1, zero
// dipendenze esterne).
import { DEFAULT_VIEW, STORAGE } from './lib/config.js';

const OSM_TILES = ['a', 'b', 'c'].map(s => `https://${s}.tile.openstreetmap.org/{z}/{x}/{y}.png`);
const ESRI_TILES = ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'];

export const BASE_STYLE = {
    version: 8,
    name: 'weathersicily-v2',
    sky: {'sky-color':'#80b5d5','horizon-color':'#e7eff4','sky-horizon-blend':0.7,'fog-color':'#e7eff4','fog-ground-blend':0.98,'horizon-fog-blend':0.2},
    sources: {
        osm: {
            type: 'raster', tiles: OSM_TILES, tileSize: 256, maxzoom: 18,
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        },
        esri: {
            type: 'raster', tiles: ESRI_TILES, tileSize: 256, maxzoom: 19,
            attribution: 'Tiles © Esri',
        },
    },
    layers: [
        { id: 'bg', type: 'background', paint: { 'background-color': '#aad3df' } },
        { id: 'base-osm', type: 'raster', source: 'osm', layout: { visibility: 'visible' } },
        { id: 'base-esri', type: 'raster', source: 'esri', layout: { visibility: 'none' } },
    ],
};

export function createMap(container, view = {}) {
    const map = new maplibregl.Map({
        container,
        style: BASE_STYLE,
        center: view.center || DEFAULT_VIEW.center,
        zoom: view.zoom ?? DEFAULT_VIEW.zoom,
        minZoom: DEFAULT_VIEW.minZoom,
        maxZoom: DEFAULT_VIEW.maxZoom,
        maxPitch: 65,
        attributionControl: { compact: true },
        // "Burro": rotella morbida e cooperativa col trackpad, niente snap.
        scrollZoom: true,
        dragRotate: false,        // la v1 non ruota: stessa UX
        pitchWithRotate: true,
        touchPitch: false,
        fadeDuration: 220,
        maxTileCacheSize: 512,
        // Default MapLibre 4096: a schermo intero su monitor grandi/dpr alto il
        // canvas viene CLAMPATO e stirato → pallini sgranati (bug Giuseppe).
        maxCanvasSize: [8192, 8192],
        cancelPendingTileRequestsWhileZooming: false,
    });
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation();
    return map;
}

export function setBase(map, base) {
    const sat = base === 'sat';
    map.setLayoutProperty('base-osm', 'visibility', sat ? 'none' : 'visible');
    map.setLayoutProperty('base-esri', 'visibility', sat ? 'visible' : 'none');
    try { localStorage.setItem(STORAGE.base, sat ? 'sat' : 'osm'); } catch (e) {}
    document.documentElement.classList.toggle('wsv2-base-sat', sat);
}

// Tema scuro: i tile OSM vengono attenuati via paint GPU (niente CSS filter
// sul canvas: costerebbe una passata di compositing in più a ogni frame).
export function applyMapTheme(map, dark) {
    const props = dark
        ? { 'raster-brightness-max': 0.62, 'raster-saturation': -0.45, 'raster-contrast': 0.08 }
        : { 'raster-brightness-max': 1, 'raster-saturation': 0, 'raster-contrast': 0 };
    for (const [k, v] of Object.entries(props)) map.setPaintProperty('base-osm', k, v);
}

// Tile downloads and terrain do not determine whether a parsed style can accept layers.
export function isMapStyleReady(map) {
    // MapLibre temporarily detaches its style during WebGL recovery.
    // Async weather updates must wait rather than throw at that point.
    try { return !!map?.getLayer('base-esri'); } catch { return false; }
}
